import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import ReactDOMServer from 'react-dom/server'
import { makeStyles } from '@material-ui/core/styles'
import { Typography, Box } from '@material-ui/core'
import { Route } from '../../services/api.types'
import { c, t } from 'ttag'
import { MapContainer, Polyline, Marker, Popup, CircleMarker, useMap } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import 'maplibre-gl/dist/maplibre-gl.css'
import '@maplibre/maplibre-gl-leaflet'
import { tokens } from '../../styles/tokens'
// Import SF Symbol SVG files as React components and URLs
import { ReactComponent as PlayFillIcon } from '../../assets/sf-symbols/play.fill.svg'
import { ReactComponent as StopFillIcon } from '../../assets/sf-symbols/stop.fill.svg'

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
})

// Create custom start and finish icons using SF Symbol React components
const createCustomIcon = (color: string, IconComponent: React.ComponentType<any>, isStart: boolean) => {
  // Create a temporary container to render the React component
  const tempContainer = document.createElement('div')

  // Render the React component to get its HTML
  const iconElement = React.createElement(IconComponent, {
    style: {
      width: '10px',
      height: '10px',
      fill: 'white',
      display: 'block',
      padding: '1px',
    },
  })

  // Use ReactDOM to render the component to HTML string
  const iconHtml = ReactDOMServer.renderToString(iconElement)

  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        color: white;
        background-color: ${color};
        width: 20px;
        height: 20px;
        border-radius: 50%;
        border: 2px solid white;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        position: relative;
      ">
        ${iconHtml}
      </div>
    `,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  })
}

const useStyles = makeStyles((theme) => ({
  routeMapContainer: {
    // Remove padding and borders for cleaner look
  },
  mapContainer: {
    height: '300px',
    width: '100%',
    borderRadius: '8px',
    border: `1px solid ${theme.palette.grey[200]}`,
    overflow: 'hidden',
  },
  noRouteData: {
    textAlign: 'center',
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    padding: '20px',
  },
}))

interface RouteMapProps {
  routes: Route[]
  activityId: string
  /**
   * Elapsed workout seconds hovered on the route stats chart; the map shows a position marker at
   * the matching route point (null hides it).
   */
  highlightElapsedSeconds?: number | null
}

// On touch devices one-finger drag must scroll the page, not pan the map; a deliberate
// two-finger pinch (touchZoom) still zooms and pans it.
const isCoarsePointer = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches

// OpenFreeMap vector basemap (free, no API key, commercial use allowed)
const BASEMAP_STYLE_URL = 'https://tiles.openfreemap.org/styles/positron'

// Minimal attribution required by the data licenses (ODbL for OSM data, CC-BY for OpenMapTiles)
const BASEMAP_ATTRIBUTION =
  '&copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'

// Renders MapLibre GL vector tiles as a Leaflet layer
function MapLibreLayer({ styleUrl }: { styleUrl: string }) {
  const map = useMap()

  useEffect(() => {
    // Drop Leaflet's default "Leaflet" prefix from the attribution control
    map.attributionControl?.setPrefix('')
    const layer = L.maplibreGL({
      style: styleUrl,
      attributionControl: { customAttribution: BASEMAP_ATTRIBUTION },
    })
    layer.addTo(map)
    return () => {
      // Abort any in-flight zoom animation before teardown. The map unmounts when scrolled
      // out of view, and a zoom transitionend arriving after map.remove() crashes Leaflet
      // ("undefined is not an object evaluating e._leaflet_pos") reading the deleted map pane.
      ;(map as any)._animatingZoom = false
      map.removeLayer(layer)
    }
  }, [map, styleUrl])

  return null
}

// Desktop wheel handling: a plain scroll must keep scrolling the page (the map sits inline in the
// activities list), so Leaflet's scrollWheelZoom stays off. Zoom only on Ctrl/Cmd + wheel — which
// is also what browsers report for a trackpad pinch, so pinch-to-zoom keeps working.
function CooperativeWheelZoom() {
  const map = useMap()

  useEffect(() => {
    const container = map.getContainer()
    const onWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return
      // Zooming, not page-scrolling (or browser pinch-zooming the whole page).
      event.preventDefault()
      const zoom = map.getZoom() - Math.sign(event.deltaY)
      map.setZoomAround(map.mouseEventToContainerPoint(event as any), zoom)
    }
    container.addEventListener('wheel', onWheel, { passive: false })
    return () => container.removeEventListener('wheel', onWheel)
  }, [map])

  return null
}

// Component to handle map bounds updates
function MapBoundsUpdater({ bounds }: { bounds: L.LatLngBounds | null }) {
  const map = useMap()

  useEffect(() => {
    if (bounds) {
      // animate: false — the fit happens on every (re)mount while scrolling the list, and an
      // animated zoom can still be running when the map unmounts again (see MapLibreLayer cleanup).
      // maxZoom — a single-point route has zero-size bounds, for which Leaflet computes an
      // Infinity zoom, poisoning every later projection with NaN (broken, unzoomable map).
      map.fitBounds(bounds, { padding: [20, 20], animate: false, maxZoom: 17 })
    }
  }, [bounds, map])

  return null
}

// Component to handle map initialization and prevent re-creation
function MapInitializer({
  onMapReady,
  mapRef,
}: {
  onMapReady: () => void
  mapRef: React.MutableRefObject<L.Map | null>
}) {
  const map = useMap()

  useEffect(() => {
    mapRef.current = map
    onMapReady()
  }, [map, onMapReady, mapRef])

  return null
}

/**
 * Interactive map component that displays GPS route points using OpenStreetMap
 * Shows the route as a polyline with start/end markers and route statistics
 */
export default function RouteMap({ routes, activityId, highlightElapsedSeconds }: RouteMapProps) {
  const classes = useStyles()
  const [mapCenter, setMapCenter] = useState<[number, number]>([0, 0])
  const [mapBounds, setMapBounds] = useState<L.LatLngBounds | null>(null)
  const [startIcon, setStartIcon] = useState<L.DivIcon | null>(null)
  const [finishIcon, setFinishIcon] = useState<L.DivIcon | null>(null)
  const [mapInitialized, setMapInitialized] = useState(false)
  const mapRef = useRef<L.Map | null>(null)
  const visibilityRef = useRef<HTMLDivElement>(null)
  const [isNearViewport, setIsNearViewport] = useState(false)

  // The route point closest to the chart's hovered elapsed time (routes arrive time-ordered).
  const highlightPoint = useMemo(() => {
    if (highlightElapsedSeconds == null || !routes.length) return null
    const targetTs = routes[0].ts + highlightElapsedSeconds * 1000
    let best = routes[0]
    let bestDelta = Infinity
    routes.forEach((point) => {
      const delta = Math.abs(point.ts - targetTs)
      if (delta < bestDelta) {
        bestDelta = delta
        best = point
      }
    })
    return best
  }, [routes, highlightElapsedSeconds])

  // Only mount the map while it is near the viewport. Browsers allow only a handful of
  // active WebGL contexts per page, so rendering every map in a long activity list at
  // once makes the browser silently drop the oldest ones (blank white maps).
  useEffect(() => {
    const el = visibilityRef.current
    if (!el) return
    const observer = new IntersectionObserver(([entry]) => setIsNearViewport(entry.isIntersecting), {
      rootMargin: '300px 0px',
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [routes.length])

  // Handle map initialization
  const handleMapReady = useCallback(() => {
    if (!mapInitialized) {
      setMapInitialized(true)
    }
  }, [mapInitialized])

  // Create icons when component mounts
  useEffect(() => {
    const createIcons = () => {
      try {
        // Use the imported React components directly
        const start = createCustomIcon('#4CAF50', PlayFillIcon, true)
        const finish = createCustomIcon('#F44336', StopFillIcon, false)
        setStartIcon(start)
        setFinishIcon(finish)
      } catch (error) {
        console.error('Failed to create map icons:', error)
      }
    }

    createIcons()
  }, [])

  // Calculate route statistics
  const totalPoints = routes.length
  const hasAltitude = routes.some((route) => route.alt !== undefined)
  const hasSpeed = routes.some((route) => route.spd !== undefined)

  // Calculate bounding box and center for the route
  useEffect(() => {
    if (routes.length === 0) return

    const lats = routes.map((route) => route.lat)
    const lngs = routes.map((route) => route.lng)
    const minLat = Math.min(...lats)
    const maxLat = Math.max(...lats)
    const minLng = Math.min(...lngs)
    const maxLng = Math.max(...lngs)

    // Set map center to the middle of the route
    const centerLat = (minLat + maxLat) / 2
    const centerLng = (minLng + maxLng) / 2

    // Validate coordinates - if they're invalid, use a default location
    if (isNaN(centerLat) || isNaN(centerLng) || centerLat === 0 || centerLng === 0) {
      console.warn('Invalid coordinates detected, using default location')
      setMapCenter([40.7128, -74.006]) // New York City as fallback
    } else {
      setMapCenter([centerLat, centerLng])
    }

    // Set bounds for the map to fit the entire route
    const bounds = L.latLngBounds(L.latLng(minLat, minLng), L.latLng(maxLat, maxLng))
    setMapBounds(bounds)
  }, [routes])

  // Calculate approximate distance using Haversine formula
  const calculateDistance = () => {
    if (routes.length < 2) return 0

    let totalDistance = 0
    for (let i = 1; i < routes.length; i++) {
      const prev = routes[i - 1]
      const curr = routes[i]

      // Haversine formula for more accurate distance calculation
      const R = 6371000 // Earth's radius in meters
      const φ1 = (prev.lat * Math.PI) / 180
      const φ2 = (curr.lat * Math.PI) / 180
      const Δφ = ((curr.lat - prev.lat) * Math.PI) / 180
      const Δλ = ((curr.lng - prev.lng) * Math.PI) / 180

      const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

      totalDistance += R * c
    }

    return totalDistance
  }

  const totalDistance = calculateDistance()
  const distanceKm = (totalDistance / 1000).toFixed(2)

  // Convert routes to polyline coordinates
  const polylinePositions: [number, number][] = routes.map((route) => [route.lat, route.lng])

  if (routes.length === 0) {
    return (
      <div className={classes.routeMapContainer}>
        <div className={classes.noRouteData}>{t`No route data available for this activity`}</div>
      </div>
    )
  }

  return (
    <div className={classes.routeMapContainer}>
      <div className={classes.mapContainer} ref={visibilityRef}>
        {isNearViewport && (
          <MapContainer
            key={`map-${activityId}`}
            center={mapCenter}
            zoom={13}
            style={{ height: '100%', width: '100%' }}
            // Plain wheel scroll must never zoom (it would hijack page scrolling on desktop);
            // CooperativeWheelZoom below zooms on Ctrl/Cmd + wheel and trackpad pinch instead.
            // On touch devices one-finger scrolling stays free too (two-finger touchZoom works).
            scrollWheelZoom={false}
            dragging={!isCoarsePointer}
            touchZoom={true}
            doubleClickZoom={true}
            zoomControl={false}
            whenReady={handleMapReady}
          >
            {!isCoarsePointer && <CooperativeWheelZoom />}
            <MapLibreLayer styleUrl={BASEMAP_STYLE_URL} />

            {/* Map initializer */}
            <MapInitializer onMapReady={handleMapReady} mapRef={mapRef} />

            {/* Map bounds updater */}
            <MapBoundsUpdater bounds={mapBounds} />

            {/* Route polyline with white casing for contrast on the light basemap */}
            <Polyline positions={polylinePositions} color="#FFFFFF" weight={7} opacity={0.9} />
            <Polyline positions={polylinePositions} color={tokens.brand.blue} weight={4} opacity={1} />

            {/* Start marker */}
            {routes.length > 0 && startIcon && (
              <Marker position={[routes[0].lat, routes[0].lng]} icon={startIcon}>
                <Popup>
                  <div>
                    <strong>{c('Route point').t`Start`}</strong>
                    <br />
                    {t`Time`}: {new Date(routes[0].ts).toLocaleString()}
                    {routes[0].alt && (
                      <>
                        <br />
                        {t`Altitude`}: {routes[0].alt.toFixed(1)}m
                      </>
                    )}
                  </div>
                </Popup>
              </Marker>
            )}

            {/* End marker */}
            {routes.length > 1 && finishIcon && (
              <Marker position={[routes[routes.length - 1].lat, routes[routes.length - 1].lng]} icon={finishIcon}>
                <Popup>
                  <div>
                    <strong>{c('Route point').t`End`}</strong>
                    <br />
                    {t`Time`}: {new Date(routes[routes.length - 1].ts).toLocaleString()}
                    {routes[routes.length - 1].alt && (
                      <>
                        <br />
                        {t`Altitude`}: {routes[routes.length - 1].alt?.toFixed(1)}m
                      </>
                    )}
                  </div>
                </Popup>
              </Marker>
            )}

            {/* Position marker following the hovered time on the route stats chart */}
            {highlightPoint && (
              <CircleMarker
                center={[highlightPoint.lat, highlightPoint.lng]}
                radius={10}
                color="#FFFFFF"
                weight={2}
                fillColor={tokens.brand.blue}
                fillOpacity={1}
              />
            )}
          </MapContainer>
        )}
      </div>
    </div>
  )
}
